<template>
  <section>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <h1 class="title">{{ $t('entity.sessions') }}</h1>
        <button
          v-if="thereAreActiveSessions"
          class="btn btn-outline-primary ml-4"
          @click="goToActiveSessions"
        >
          {{ $t('sessionStatus.activeNow') }}
        </button>
      </div>
      <div class="d-flex align-items-center">
        <statical-data v-if="isHospitalAdmin || isNeuroAdmin"></statical-data>
        <session-filter
          :title="$t('src/components/filters/pagesessionfilter.sessionFilters')"
          :person="filteredPerson"
          :id="trainingId"
          :statuses="statuses"
          :events="events"
          :dates="dates"
          @confirm="confirmFilter"
        ></session-filter>
      </div>
    </div>
    <filter-result
      v-if="filterResultData && isReady"
      :data="filterResultData"
      :headers="headers"
    ></filter-result>
    <base-table
      :headers="headers"
      :no-data="noTrainings"
      :loading="loading"
      :sorting="sorting"
      @filter="filterByTableSelect"
      @sorting="changeSorting"
      @isReady="isReadyTable"
    >
      <template #tbody>
        <trainings-table-row
          v-for="training in trainings"
          :key="training.id"
          :training="training"
        ></trainings-table-row>
      </template>
    </base-table>
    <div class="mt-3">
      <paginator
        v-if="showPaginator"
        :total="totalCount"
        :per-page="perPage"
        v-model="currentPage"
        @change:count="numberOfElements"
      ></paginator>
    </div>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { status, eventTypes, DATE_FORMAT, FIND_BY_PERSON } from '@/main.config.js'
import { eventsConfig } from '@/events.config.js'
import { stringify } from 'qs'
import { paginator } from '@/mixins/paginator.js'
import { subscribeMobileEvents } from '@/mixins/subscribe-mobile-events.js'
import { getStatusListForFilter, sessionTypeList } from '@/modules/status-list.js'
import { pageFiltersInQuery } from '@/components/Filters/mixins/page-filters-in-query'
import { userRights } from '@/mixins/user-rights'

export default {
  name: 'TrainingsList',
  mixins: [paginator, subscribeMobileEvents, pageFiltersInQuery, userRights],
  components: {
    BaseTable: () => import('@/base/BaseTable.vue'),
    TrainingsTableRow: () => import('@/components/table-rows/TrainingsTableRow'),
    SessionFilter: () => import('@/components/Filters/PageSessionFilter.vue'),
    FilterResult: () => import('@/components/Filters/FilterResult.vue'),
    StaticalData: () => import('@/components/StaticalData.vue')
  },
  data: () => ({
    loading: true,
    filteredPerson: {
      id: null
    },
    trainingId: undefined,
    statuses: [status.done],
    trainings: null,
    dates: null,
    events: [],
    sorting: {
      by: 'date',
      order: 'desc'
    },
    comments: null,
    isReady: false,
    lastDataTime: null,
    trainingTypes: [],
    thereAreActiveSessions: false
  }),
  computed: {
    statusList() {
      return getStatusListForFilter('session')
    },
    typeList() {
      return sessionTypeList
    },
    hasFilterRange() {
      return Boolean(this.dates && this.dates.startDate && this.dates.endDate)
    },
    paramsDateCondition() {
      if (!this.hasFilterRange) {
        return null
      }
      const dateEnd = this.dates.endDate.clone().add(1, 'day')
      return {
        $gte: this.dates.startDate.format(DATE_FORMAT.serverFull),
        $lt: dateEnd.format(DATE_FORMAT.serverFull)
      }
    },
    sortParams() {
      const sortOrder = this.sorting.order === 'asc' ? 1 : -1
      if (this.sorting.by === 'date') {
        return {
          date_start: sortOrder
        }
      } else if (this.sorting.by === 'patient') {
        return {
          'patient.surname': sortOrder,
          'patient.name': sortOrder
        }
      } else if (this.sorting.by === 'doctor') {
        return {
          'user.surname': sortOrder,
          'user.name': sortOrder
        }
      }
      return {
        id: sortOrder
      }
    },
    params() {
      let params = Object.assign({}, this.pageParams)
      params['$sort'] = this.sortParams
      if (this.statuses.length >= 1) {
        params['training.status'] = {
          $in: this.statuses
        }
      } else if (Object.prototype.hasOwnProperty.call(params, 'training.status')) {
        delete params['training.status']
      }

      if (this.trainingTypes.length >= 1) {
        params['training_type'] = {
          $in: this.trainingTypes
        }
      } else if (Object.prototype.hasOwnProperty.call(params, 'training_type')) {
        delete params['training_type']
      }

      if (this.filteredPerson.id) {
        if (this.filteredPerson.findBy === FIND_BY_PERSON.patient) {
          params.patient_id = this.filteredPerson.id
          if (Object.prototype.hasOwnProperty.call(params, 'doctor_id')) {
            delete params['doctor_id']
          }
        } else if (this.filteredPerson.findBy === FIND_BY_PERSON.doctor) {
          params.doctor_id = this.filteredPerson.id
          if (Object.prototype.hasOwnProperty.call(params, 'patient_id')) {
            delete params['patient_id']
          }
        }
      } else {
        if (Object.prototype.hasOwnProperty.call(params, 'patient_id')) {
          delete params['patient_id']
        }
        if (Object.prototype.hasOwnProperty.call(params, 'doctor_id')) {
          delete params['doctor_id']
        }
      }

      if (this.$store.getters.IS_SELECTED_HOSPITAL) {
        params['patient.hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      if (this.trainingId) {
        params['training.id'] = this.trainingId
      }

      if (this.hasFilterRange) {
        params['training.date_start'] = this.paramsDateCondition
      }

      if (this.events.length === 1) {
        params['training.events'] = {
          $like: `%${this.events[0]}%`
        }
      } else if (this.events.length > 1) {
        const eventsLike = this.events.map(event => ({
          'training.events': { $like: `%${event}%` }
        }))
        params['$or'] = eventsLike
      } else if (Object.prototype.hasOwnProperty.call(params, 'training.events')) {
        delete params['training.events']
      }

      if (this.comments) {
        params['comments'] = {
          $like: `%${this.comments}%`
        }
      } else if (Object.prototype.hasOwnProperty.call(params, 'comments')) {
        delete params['comments']
      }

      return params
    },
    noTrainings() {
      return !this.trainings || this.trainings.length === 0
    },
    filterResultData() {
      let data = {}

      if (this.statuses.length > 0) {
        data[this.$t('general.status')] = this.statuses.map(item => this.statusList[item].title)
      }

      if (this.hasFilterRange) {
        data[this.$t('sessionsInfo.date&Time')] =
          this.dates.startDate.format('DD.MM.YY') + '-' + this.dates.endDate.format('DD.MM.YY')
      }

      if (this.trainingId) {
        data['ID'] = this.trainingId
      }

      if (this.filteredPerson.id) {
        const title =
          this.filteredPerson.findBy === FIND_BY_PERSON.patient
            ? this.$t('entity.patient')
            : this.$t('general.therapist')
        data[title] = this.filteredPerson.name
      }

      if (this.events.length > 0) {
        data[this.$t('sessionsInfo.events')] = this.events.map(event => eventsConfig[event].abbr)
      }

      if (this.trainingTypes.length > 0) {
        data[this.$t('general.type')] = this.trainingTypes.map(item => this.typeList[item].title)
      }

      return data
    },
    headers() {
      return {
        patient: {
          title: this.$t('entity.patient'),
          class: 'th-name',
          sorting: 'asc'
        },
        date: {
          title: this.$t('sessionsInfo.date&Time'),
          class: 'th-date-long',
          sorting: 'desc'
        },
        heartRate: {
          title: this.$t('sessionsInfo.heartRate')
        },
        doctor: {
          title: this.$t('general.therapist'),
          class: 'th-name',
          sorting: 'asc'
        },
        events: {
          title: this.$t('sessionsInfo.events'),
          class: 'th-event'
        },
        status: {
          title: this.$t('general.status'),
          class: 'th-mid',
          filter: true,
          options: this.statusList,
          selected: this.statuses.length === 1 ? this.statuses[0] : null
        },
        id: {
          title: 'ID',
          sorting: 'asc'
        }
      }
    }
  },
  mounted() {
    this.loadData().then(() => {
      this.canSubscribe = true
    })
    this.loadActiveSessions()
  },
  methods: {
    loadData() {
      this.loading = true
      this.isReady = false

      return new Promise(resolve => {
        http
          .get('/trainings', {
            params: this.params,
            paramsSerializer: params =>
              stringify(params, {
                arrayFormat: 'indices',
                encodeValuesOnly: true
              })
          })
          .then(response => response.data)
          .then(trainings => {
            this.totalCount = trainings.total
            this.trainings = trainings.data
            resolve()
          })
          .catch(error => console.log(error))
          .finally(() => (this.loading = false))
      })
    },
    loadActiveSessions() {
      let params = {
        'training.status': {
          $in: [2]
        }
      }
      if (this.$store.getters.IS_SELECTED_HOSPITAL) {
        params['patient.hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      http
        .get('/trainings', {
          params: params,
          paramsSerializer: params =>
            stringify(params, {
              arrayFormat: 'indices',
              encodeValuesOnly: true
            })
        })
        .then(response => {
          this.thereAreActiveSessions = response.data.total > 0 ? true : false
        })
        .catch(error => console.log(error))
    },
    confirmFilter(data) {
      this.filteredPerson = data.person
      this.trainingId = data.trainingId
      this.statuses = data.statuses
      this.dates = data.dates
      this.events = data.events
      this.comments = data.comments
      this.trainingTypes = data.types

      this.currentPage = 1
      this.updateFullQuery()
      this.loadData()
    },
    filterByTableSelect(event) {
      if (event.key === 'status') {
        const status = parseInt(event.value, 10)
        this.statuses = status !== 0 ? [status] : []
      }
      this.currentPage = 1
      this.updateFullQuery()
      this.loadData()
    },
    changeSorting(sortingData) {
      this.sorting = sortingData
      this.loadData()
    },
    changeDataByEvent(event) {
      this.lastDataTime = event.type === 'SR' && event.value === 0 ? this.lastDataTime : event.time
      localStorage.setItem(`lastDataTime${event.tr_id}`, this.lastDataTime)

      let coincide = this.trainings.filter((training, index) => {
        if (+training.id === +event.tr_id) {
          this.changeTraining(index, event)
          return true
        }
      })

      if (!coincide.length) {
        this.doIfNoCoincideTrainings(event)
      }
    },
    changeTraining(index, event) {
      if (this.trainings.status !== status.active) {
        this.trainings[index].status = status.active
      }

      switch (event.type) {
        case eventTypes.changeHR:
          this.trainings[index].heart_rate_min = event.value
          break
        case eventTypes.cardioEvent:
          this.addCardioEvent(event, index)
          break
        case eventTypes.stopTraining:
          this.trainings[index].status = status.done
          this.reLoadTraining(index)
          break
      }
    },
    addCardioEvent(event, index) {
      let parsedEvents = JSON.parse(this.trainings[index].events)
      if (!parsedEvents) {
        parsedEvents = []
      }

      parsedEvents.push({
        beginTime: event.time,
        episodeType: event.episodeType
      })
      this.trainings[index].events = JSON.stringify(parsedEvents)
    },
    doIfNoCoincideTrainings(event) {
      this.addToStack(event)

      /*Если текущий статус "Идущие тренировки", а изменеямой трени в списке нет, надо перегрузить,
       * после чего проверить осталось ли в стеке что-то для изменения (если пришли еще значения, то стек для этой трени очистится)*/
      if (this.statuses.includes(status.active)) {
        this.loadData().then(() => {
          if (this.stack[event.tr_id]) {
            this.changeDataByEvent(event)
          }
        })
      }
    },
    reLoadTraining(index) {
      http
        .get('/trainings/' + this.trainings[index]['id'])
        .then(response => response.data)
        .then(training => {
          this.trainings[index] = training
        })
        .catch(error => console.log(error))
    },
    numberOfElements(number) {
      this.perPage = number
    },
    isReadyTable(value) {
      this.isReady = value
    },
    goToActiveSessions() {
      this.$router.push({ name: 'active-sessions-multiple' })
    }
  }
}
</script>
